import React, { useEffect, useMemo, useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import {
    addNetwork,
    fetchNetworkFeatureCatalog,
    fetchNetworks,
    updateNetwork,
} from "../services/networks";
import AdminCardActions from "../components/AdminCardActions";
import ToolbarSearch from "../components/ToolbarSearch";

const DEPLOYMENT_SAAS = "saas";
const DEPLOYMENT_SELF_HOSTED = "self_hosted";
const MOBILE_NONE = "none";
const MOBILE_PROVIDER = "provider";

function buildDefaultFeatures(catalog) {
    const map = {};
    (catalog || []).forEach((item) => {
        map[item.key] = item.default_enabled !== false;
    });
    if (Object.prototype.hasOwnProperty.call(map, "sensors")) map.sensors = false;
    if (Object.prototype.hasOwnProperty.call(map, "door_access")) map.door_access = false;
    if (Object.prototype.hasOwnProperty.call(map, "camera_access")) map.camera_access = false;
    return map;
}

function defaultMobileForDeployment(mode) {
    return mode === DEPLOYMENT_SELF_HOSTED ? MOBILE_NONE : MOBILE_PROVIDER;
}

function FeatureChecklist({ catalog, features, onChange, disabled }) {
    const groups = useMemo(() => {
        const byGroup = {};
        (catalog || []).forEach((item) => {
            const g = item.group || "other";
            if (!byGroup[g]) {
                byGroup[g] = {
                    label: item.group_label || g,
                    items: [],
                };
            }
            byGroup[g].items.push(item);
        });
        return Object.values(byGroup);
    }, [catalog]);

    if (!catalog?.length) return null;

    return (
        <div className="network-features-box">
            <div className="network-features-title">Модулі / довідники мережі</div>
            <p className="network-features-hint">
                Відмічені пункти зʼявляться в меню оператора та вплинуть на видимість сторінок клієнтів цієї мережі.
            </p>
            {groups.map((group) => (
                <div key={group.label} className="network-features-group">
                    <div className="network-features-group-title">{group.label}</div>
                    <div className="network-features-grid">
                        {group.items.map((item) => (
                            <label key={item.key} className="network-feature-item">
                                <input
                                    type="checkbox"
                                    checked={!!features?.[item.key]}
                                    disabled={disabled}
                                    onChange={(e) => onChange(item.key, e.target.checked)}
                                />
                                <span>{item.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function DeploymentPanel({ value, onChange, disabled }) {
    const mode = value?.deployment_mode || DEPLOYMENT_SAAS;
    const mobile = value?.mobile_apps || defaultMobileForDeployment(mode);
    const isSelfHosted = mode === DEPLOYMENT_SELF_HOSTED;

    const setField = (patch) => {
        onChange({
            ...(value || {}),
            ...patch,
        });
    };

    const onModeChange = (nextMode) => {
        setField({
            deployment_mode: nextMode,
            mobile_apps: defaultMobileForDeployment(nextMode),
            self_hosted_notes:
                nextMode === DEPLOYMENT_SELF_HOSTED ? (value?.self_hosted_notes || "") : "",
        });
    };

    return (
        <div className={`network-deploy-box ${isSelfHosted ? "is-self-hosted" : ""}`}>
            <div className="network-deploy-title">Розгортання та мобільний доступ</div>
            <p className="network-deploy-hint">
                Оберіть, де живуть дані мережі. Self-hosted не створює суперадміна на стороні клієнта —
                мережу й ліцензію налаштовуєте ви.
            </p>

            <div className="network-deploy-options" role="radiogroup" aria-label="Режим розгортання">
                <label className={`network-deploy-option ${mode === DEPLOYMENT_SAAS ? "is-active" : ""}`}>
                    <input
                        type="radio"
                        name="deployment_mode"
                        value={DEPLOYMENT_SAAS}
                        checked={mode === DEPLOYMENT_SAAS}
                        disabled={disabled}
                        onChange={() => onModeChange(DEPLOYMENT_SAAS)}
                    />
                    <span className="network-deploy-option-body">
                        <strong>У провайдера (SaaS)</strong>
                        <span>Дані в вашій хмарі. Звичайний сценарій, спільний мобільний додаток.</span>
                    </span>
                </label>
                <label className={`network-deploy-option ${isSelfHosted ? "is-active" : ""}`}>
                    <input
                        type="radio"
                        name="deployment_mode"
                        value={DEPLOYMENT_SELF_HOSTED}
                        checked={isSelfHosted}
                        disabled={disabled}
                        onChange={() => onModeChange(DEPLOYMENT_SELF_HOSTED)}
                    />
                    <span className="network-deploy-option-body">
                        <strong>У клієнта (self-hosted)</strong>
                        <span>API + БД + веб на сервері клієнта. Ви лише реєструєте мережу й керуєте ліцензією.</span>
                    </span>
                </label>
            </div>

            <label className="network-deploy-field">
                <span>Мобільний додаток провайдера</span>
                <select
                    value={mobile}
                    disabled={disabled}
                    onChange={(e) => setField({ mobile_apps: e.target.value })}
                >
                    <option value={MOBILE_NONE}>Не потрібен (веб-кабінет у клієнта)</option>
                    <option value={MOBILE_PROVIDER}>
                        Потрібен (етап 2 — спільний APK через провайдера)
                    </option>
                </select>
            </label>

            {isSelfHosted && mobile === MOBILE_NONE ? (
                <div className="network-deploy-callout">
                    <strong>Етап 1 — лише ліцензія</strong>
                    <ul>
                        <li>Клієнт користується веб-кабінетом на своєму сервері.</li>
                        <li>Видавайте / продовжуйте / відкликайте ліцензію на сторінці «Ліцензія» для цієї мережі.</li>
                        <li>На інстансі клієнта не створюють суперадмінів — лише адмін мережі / оператори.</li>
                    </ul>
                </div>
            ) : null}

            {isSelfHosted && mobile === MOBILE_PROVIDER ? (
                <div className="network-deploy-callout is-warn">
                    <strong>Мобільний доступ — етап 2</strong>
                    <p>
                        Прапорець збережено як намір. Зараз спільний APK ще не маршрутизує self-hosted мережі.
                        Клієнт може користуватись веб-кабінетом; gateway і URL upstream зʼявляться пізніше.
                    </p>
                </div>
            ) : null}

            {isSelfHosted ? (
                <label className="network-deploy-field">
                    <span>Нотатки для вас (URL адмінки клієнта, контакт, середовище)</span>
                    <textarea
                        rows={3}
                        maxLength={1000}
                        disabled={disabled}
                        placeholder="Наприклад: https://osbb-example.ua/admin · контакт Іван · prod VPS"
                        value={value?.self_hosted_notes || ""}
                        onChange={(e) => setField({ self_hosted_notes: e.target.value })}
                    />
                </label>
            ) : null}
        </div>
    );
}

function DeploymentBadges({ network }) {
    const mode = network?.deployment_mode || DEPLOYMENT_SAAS;
    const mobile = network?.mobile_apps || defaultMobileForDeployment(mode);
    return (
        <div className="network-badges">
            <span className={`network-badge ${mode === DEPLOYMENT_SELF_HOSTED ? "is-self-hosted" : "is-saas"}`}>
                {mode === DEPLOYMENT_SELF_HOSTED ? "Self-hosted" : "SaaS"}
            </span>
            <span className={`network-badge ${mobile === MOBILE_PROVIDER ? "is-mobile-on" : "is-mobile-off"}`}>
                {mobile === MOBILE_PROVIDER ? "Мобільний (план)" : "Без мобільного"}
            </span>
        </div>
    );
}

const NetworksAdmins = ({ isSuperAdmin = false }) => {
    const [networks, setNetworks] = useState([]);
    const [filteredNetworks, setFilteredNetworks] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [edited, setEdited] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [deploymentFilter, setDeploymentFilter] = useState("all");
    const [sortBy, setSortBy] = useState("network_name");
    const [sortDir, setSortDir] = useState("asc");
    const [showModal, setShowModal] = useState(false);
    const [newNetwork, setNewNetwork] = useState({
        network_name: "",
        descriptions: "",
        status: true,
        features: {},
        deployment_mode: DEPLOYMENT_SAAS,
        mobile_apps: MOBILE_PROVIDER,
        self_hosted_notes: "",
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        loadNetworks();
        loadCatalog();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = networks.filter((n) => {
            const matchesSearch =
                (n.network_name || "").toString().toLowerCase().includes(q) ||
                (n.descriptions || "").toString().toLowerCase().includes(q) ||
                (n.self_hosted_notes || "").toString().toLowerCase().includes(q);
            if (!matchesSearch) return false;
            if (deploymentFilter === "all") return true;
            return (n.deployment_mode || DEPLOYMENT_SAAS) === deploymentFilter;
        });
        setFilteredNetworks(filtered);
    }, [networks, searchQuery, deploymentFilter]);

    const loadCatalog = async () => {
        try {
            const data = await fetchNetworkFeatureCatalog();
            const list = Array.isArray(data) ? data : [];
            setCatalog(list);
            setNewNetwork((prev) => ({
                ...prev,
                features: Object.keys(prev.features || {}).length
                    ? prev.features
                    : buildDefaultFeatures(list),
            }));
        } catch (_) {
            setCatalog([]);
        }
    };

    const loadNetworks = async () => {
        try {
            const data = await fetchNetworks();
            setNetworks(data);
        } catch (err) {
            alert(`❌ Помилка завантаження мереж: ${err.message}`);
        }
    };

    const handleInputChange = (setter) => (e) => {
        const { name, value } = e.target;
        setter((prev) => ({
            ...prev,
            [name]: name === "status" ? value === "true" : value,
        }));
    };

    const setFeature = (setter) => (key, checked) => {
        setter((prev) => ({
            ...prev,
            features: {
                ...(prev.features || {}),
                [key]: !!checked,
            },
        }));
    };

    const handleEdit = (network) => {
        setEditingId(network.id);
        const next = {
            ...network,
            features: { ...(network.features || {}) },
            deployment_mode: network.deployment_mode || DEPLOYMENT_SAAS,
            mobile_apps: network.mobile_apps || defaultMobileForDeployment(network.deployment_mode),
            self_hosted_notes: network.self_hosted_notes || "",
        };
        setEdited(next);
        setOriginalData(next);
    };

    const handleCancelEdit = () => {
        setEdited(originalData);
        setEditingId(null);
    };

    const buildPayload = (source) => {
        const payload = {
            network_name: source.network_name,
            descriptions: source.descriptions || "",
            status: source.status,
        };
        if (isSuperAdmin) {
            payload.features = source.features || {};
            payload.deployment_mode = source.deployment_mode || DEPLOYMENT_SAAS;
            payload.mobile_apps = source.mobile_apps || defaultMobileForDeployment(payload.deployment_mode);
            payload.self_hosted_notes =
                payload.deployment_mode === DEPLOYMENT_SELF_HOSTED
                    ? (source.self_hosted_notes || "")
                    : "";
        }
        return payload;
    };

    const handleSave = async (id) => {
        const original = networks.find((n) => n.id === id);

        if (original?.status === true && edited.status === false) {
            const confirmDisable = window.confirm(
                "Ви впевнені, що хочете деактивувати цю мережу?\nУсі пов'язані записи мають бути також деактивовані."
            );
            if (!confirmDisable) {
                setEditingId(null);
                return;
            }
        }

        try {
            await updateNetwork(id, buildPayload(edited));
            setEditingId(null);
            loadNetworks();
        } catch (err) {
            alert(`❌ Помилка: ${err.message}`);
        }
    };

    const openCreateModal = () => {
        setNewNetwork({
            network_name: "",
            descriptions: "",
            status: true,
            features: buildDefaultFeatures(catalog),
            deployment_mode: DEPLOYMENT_SAAS,
            mobile_apps: MOBILE_PROVIDER,
            self_hosted_notes: "",
        });
        setShowModal(true);
    };

    const handleAdd = async () => {
        if (!newNetwork.network_name.trim()) {
            alert("Назва мережі не може бути порожньою!");
            return;
        }
        try {
            await addNetwork(buildPayload(newNetwork));
            setShowModal(false);
            loadNetworks();
        } catch (err) {
            alert(`❌ Помилка додавання мережі: ${err.message}`);
        }
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredNetworks) return [];
        const sorted = [...filteredNetworks];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            if (typeof valA === "string") {
                valA = valA.toLowerCase();
                valB = (valB || "").toLowerCase();
            }
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const enabledLabels = (features) => {
        if (!features || !catalog.length) return "—";
        const labels = catalog
            .filter((item) => features[item.key])
            .map((item) => item.label);
        if (!labels.length) return "немає модулів";
        if (labels.length <= 4) return labels.join(", ");
        return `${labels.slice(0, 4).join(", ")} +${labels.length - 4}`;
    };

    const renderDeploymentEditor = (source, setter) => {
        if (!isSuperAdmin) return null;
        return (
            <DeploymentPanel
                value={source}
                onChange={(next) => setter((prev) => ({ ...prev, ...next }))}
            />
        );
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={openCreateModal} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadNetworks} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук мережі..."
                    />
                    <select
                        className="network-filter-select"
                        value={deploymentFilter}
                        onChange={(e) => setDeploymentFilter(e.target.value)}
                        title="Фільтр розгортання"
                        aria-label="Фільтр розгортання"
                    >
                        <option value="all">Усі типи</option>
                        <option value={DEPLOYMENT_SAAS}>Лише SaaS</option>
                        <option value={DEPLOYMENT_SELF_HOSTED}>Лише self-hosted</option>
                    </select>
                </div>
            </div>

            {showModal && (
                <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="modal-content network-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Нова мережа</h3>
                        <input
                            name="network_name"
                            type="text"
                            placeholder="Назва мережі"
                            value={newNetwork.network_name}
                            onChange={handleInputChange(setNewNetwork)}
                        />
                        <input
                            name="descriptions"
                            type="text"
                            placeholder="Опис"
                            value={newNetwork.descriptions}
                            onChange={handleInputChange(setNewNetwork)}
                        />
                        <select
                            name="status"
                            value={String(newNetwork.status)}
                            onChange={handleInputChange(setNewNetwork)}
                        >
                            <option value="true">Активна</option>
                            <option value="false">Неактивна</option>
                        </select>
                        {renderDeploymentEditor(newNetwork, setNewNetwork)}
                        {isSuperAdmin && (
                            <FeatureChecklist
                                catalog={catalog}
                                features={newNetwork.features}
                                onChange={setFeature(setNewNetwork)}
                            />
                        )}
                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {filteredNetworks.map((net) => (
                        <div key={net.id} className="config-card">
                            <div>
                                <strong>Назва:</strong>{" "}
                                {editingId === net.id ? (
                                    <input
                                        name="network_name"
                                        value={edited.network_name}
                                        onChange={handleInputChange(setEdited)}
                                    />
                                ) : (
                                    net.network_name
                                )}
                            </div>

                            <div>
                                <strong>Опис:</strong>{" "}
                                {editingId === net.id ? (
                                    <input
                                        name="descriptions"
                                        value={edited.descriptions || ""}
                                        onChange={handleInputChange(setEdited)}
                                    />
                                ) : (
                                    net.descriptions
                                )}
                            </div>

                            <div>
                                <strong>Статус:</strong>{" "}
                                {editingId === net.id ? (
                                    <select
                                        name="status"
                                        value={String(edited.status)}
                                        onChange={handleInputChange(setEdited)}
                                    >
                                        <option value="true">Активна</option>
                                        <option value="false">Неактивна</option>
                                    </select>
                                ) : (
                                    net.status ? "Активна" : "Неактивна"
                                )}
                            </div>

                            <div>
                                <strong>Розгортання:</strong>{" "}
                                {editingId === net.id && isSuperAdmin ? (
                                    renderDeploymentEditor(edited, setEdited)
                                ) : (
                                    <DeploymentBadges network={net} />
                                )}
                            </div>

                            {net.self_hosted_notes && editingId !== net.id ? (
                                <div className="network-notes-preview">
                                    <strong>Нотатки:</strong> {net.self_hosted_notes}
                                </div>
                            ) : null}

                            <div>
                                <strong>Модулі:</strong>{" "}
                                {editingId === net.id && isSuperAdmin ? (
                                    <FeatureChecklist
                                        catalog={catalog}
                                        features={edited.features}
                                        onChange={setFeature(setEdited)}
                                    />
                                ) : (
                                    enabledLabels(net.features)
                                )}
                            </div>

                            <AdminCardActions
                                editing={editingId === net.id}
                                onEdit={() => handleEdit(net)}
                                onSave={() => handleSave(net.id)}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_name")}>
                                    Назва {sortBy === "network_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("deployment_mode")}>
                                    Розгортання {sortBy === "deployment_mode" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Модулі</th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((net) => (
                                <React.Fragment key={net.id}>
                                    <tr className={net.deployment_mode === DEPLOYMENT_SELF_HOSTED ? "network-row-self-hosted" : ""}>
                                        <td>
                                            {editingId === net.id ? (
                                                <input
                                                    name="network_name"
                                                    value={edited.network_name}
                                                    onChange={handleInputChange(setEdited)}
                                                />
                                            ) : (
                                                net.network_name
                                            )}
                                        </td>
                                        <td>
                                            {editingId === net.id ? (
                                                <input
                                                    name="descriptions"
                                                    value={edited.descriptions || ""}
                                                    onChange={handleInputChange(setEdited)}
                                                />
                                            ) : (
                                                <>
                                                    <div>{net.descriptions}</div>
                                                    {net.self_hosted_notes ? (
                                                        <div className="network-notes-preview" title={net.self_hosted_notes}>
                                                            {net.self_hosted_notes}
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <DeploymentBadges network={editingId === net.id ? edited : net} />
                                        </td>
                                        <td>
                                            {editingId === net.id ? (
                                                <select
                                                    name="status"
                                                    value={String(edited.status)}
                                                    onChange={handleInputChange(setEdited)}
                                                >
                                                    <option value="true">Активна</option>
                                                    <option value="false">Неактивна</option>
                                                </select>
                                            ) : (
                                                net.status ? "Активна" : "Неактивна"
                                            )}
                                        </td>
                                        <td style={{ minWidth: 280, maxWidth: 420 }}>
                                            {editingId === net.id && isSuperAdmin ? (
                                                <FeatureChecklist
                                                    catalog={catalog}
                                                    features={edited.features}
                                                    onChange={setFeature(setEdited)}
                                                />
                                            ) : (
                                                <span title={enabledLabels(net.features)}>{enabledLabels(net.features)}</span>
                                            )}
                                        </td>
                                        <td>
                                            <AdminCardActions
                                                editing={editingId === net.id}
                                                onEdit={() => handleEdit(net)}
                                                onSave={() => handleSave(net.id)}
                                                onCancel={handleCancelEdit}
                                            />
                                        </td>
                                    </tr>
                                    {editingId === net.id && isSuperAdmin ? (
                                        <tr className="network-edit-extra-row">
                                            <td colSpan={6}>
                                                {renderDeploymentEditor(edited, setEdited)}
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default NetworksAdmins;
